import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import clsx from 'clsx';
import authApi from '../api';
import { useLoading } from '@/hooks/useLoading';
import FullScreenLoading from '@/components/common/FullLoading';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useAppStore } from '@/stores';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '@/constants/auth';
import PasswordCheck from '../components/PasswordCheck';

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, setLoading } = useLoading();
  const themeMode = useAppStore((state) => state.ui.themeMode);
  const isDark = themeMode === 'dark';

  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isTokenError, setIsTokenError] = useState(!token);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    mode: 'onChange',
  });

  const newPassword = watch('newPassword', '');

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
    uppercase: PASSWORD_PATTERN.uppercase.test(newPassword),
    lowercase: PASSWORD_PATTERN.lowercase.test(newPassword),
    number: PASSWORD_PATTERN.number.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const resetPasswordMutation = useMutation({
    mutationFn: (data: { token: string; newPassword: string }) => authApi.resetPassword(data),
    onMutate: () => setLoading(true),
    onSuccess: () => {
      setIsSuccess(true);
      toast.success(t('resetPassword.successMessage'));
      setLoading(false);
    },
    onError: () => {
      setIsTokenError(true);
      toast.error(t('resetPassword.invalidToken'));
      setLoading(false);
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!token) {
      setIsTokenError(true);
      return;
    }
    resetPasswordMutation.mutate({ token, newPassword: data.newPassword });
  };

  // Token error state
  if (isTokenError) {
    return (
      <Box
        className={clsx(
          'login-container',
          isDark ? 'login-container--dark' : 'login-container--light'
        )}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 10,
          }}
        >
          <LanguageSwitcher />
        </Box>
        <Card className={clsx('login-card', isDark ? 'login-card--dark' : 'login-card--light')}>
          <CardHeader
            sx={{ padding: 2 }}
            title={
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
                  alt="Intellio AI"
                  sx={{ height: 64, width: 'auto' }}
                />
              </Box>
            }
          />
          <CardContent>
            <Stack spacing={2} sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                {t('resetPassword.invalidTokenTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('resetPassword.invalidTokenDescription')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/forgot-password')}
                sx={{ mt: 2 }}
              >
                {t('resetPassword.requestNewLink')}
              </Button>
              <MuiLink
                href="/login"
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main',
                  textDecoration: 'none',
                  mt: 2,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('forgotPassword.backToLogin')}
              </MuiLink>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <Box
        className={clsx(
          'login-container',
          isDark ? 'login-container--dark' : 'login-container--light'
        )}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 10,
          }}
        >
          <LanguageSwitcher />
        </Box>
        <Card className={clsx('login-card', isDark ? 'login-card--dark' : 'login-card--light')}>
          <CardHeader
            sx={{ padding: 2 }}
            title={
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
                  alt="Intellio AI"
                  sx={{ height: 64, width: 'auto' }}
                />
              </Box>
            }
          />
          <CardContent>
            <Stack spacing={2} sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mx: 'auto' }} />
              <Typography variant="h6">{t('resetPassword.successTitle')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('resetPassword.successDescription')}
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
                {t('resetPassword.goToLogin')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      className={clsx(
        'login-container',
        isDark ? 'login-container--dark' : 'login-container--light'
      )}
    >
      <FullScreenLoading open={loading} />
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 10,
        }}
      >
        <LanguageSwitcher />
      </Box>
      <Card className={clsx('login-card', isDark ? 'login-card--dark' : 'login-card--light')}>
        <CardHeader
          sx={{ padding: 2 }}
          title={
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
                alt="Intellio AI"
                sx={{ height: 64, width: 'auto' }}
              />
            </Box>
          }
        />
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                {t('resetPassword.title')}
              </Typography>

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder={t('resetPassword.newPassword')}
                className="login-text-field"
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: newPassword ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                {...register('newPassword', {
                  required: t('resetPassword.validation.passwordRequired'),
                  validate: () => isPasswordValid || t('resetPassword.validation.passwordWeak'),
                })}
              />

              {/* Password requirements */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: -1 }}>
                <PasswordCheck isValid={passwordChecks.minLength} label={t('password.minLength')} />
                <PasswordCheck isValid={passwordChecks.uppercase} label={t('password.uppercase')} />
                <PasswordCheck isValid={passwordChecks.lowercase} label={t('password.lowercase')} />
                <PasswordCheck isValid={passwordChecks.number} label={t('password.number')} />
              </Box>

              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('resetPassword.confirmPassword')}
                className="login-text-field"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('confirmPassword', {
                  required: t('resetPassword.validation.confirmRequired'),
                  validate: (value) =>
                    value === newPassword || t('resetPassword.validation.passwordMismatch'),
                })}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                className="login-button"
                disabled={resetPasswordMutation.isPending || !isPasswordValid}
              >
                {resetPasswordMutation.isPending
                  ? t('resetPassword.resetting')
                  : t('resetPassword.resetButton')}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
